import { defineStore } from 'pinia'

// Services
import { ProfileService, InventoryService } from '@/api'
import {
  getProfile,
  getProfileDeposits,
  getBalance,
  createReferralCode,
  getReferralStats,
  useReferralCode,
  claimReferralEarnings,
  claimDepositStreak,
} from '@/services/api'
// Models
import {
  User,
  UserReferral,
  DepositHistoryProfile,
  DepositStreakStatusResponse,
  UserStats,
} from '@/services/models/models'
import type { ProfileDepositsParams, ProfileDepositsResponse } from '@/services/models/response'
import type { CaseItem } from '@/store/cases'
import type { WonItem } from '@/api/models/WonItem'
import { InventoryItem } from '@/api'
import { notify } from '@/utils/notify'
import type { UserWPStats } from '@/types/promotionalEvents'
import { useFeatureFlagsStore } from '@/store/featureFlags'

interface ProfileState {
  profilePageLoading: boolean
  profileLoading: boolean
  profile: User | null
  userReferralStats: UserReferral | null
  inventory: InventoryItem[] | null
  myStats: {
    spent: number
    won: number
    totalCaseOpenings: number
    totalUpgrades: number
    totalContracts: number
  }
  eventStats: UserWPStats | null
  prizeItemData: PrizeItemData
  deposits: DepositHistoryProfile[]
  depositsPagination: ProfileDepositsResponse['pagination'] | null
  depositStreak: DepositStreakStatusResponse | null
  stats: UserStats | null
  redepositNextAvailableAt: string | number | null
}

interface PrizeItemData {
  leveledUp: boolean
  items: any
  openAgainConfig: PrizeItemOpenAgainConfig | null
}

interface PrizeItemOpenAgainConfig {
  caseData: any
  caseItems: CaseItem[]
  amount: number
  useCaseToken: boolean
  unitPrice: number
  totalCost: number
  onAnimationStart?: () => void
  onAnimationEnd?: (items: WonItem[]) => void
  syncAmountSelection?: (amount: number) => void
  previewRouletteItems?: CaseItem[]
  previewRoulettes?: CaseItem[][]
  durationMultiplier?: number
}

export const useProfileStore = defineStore('profile', {
  state: (): ProfileState => ({
    profilePageLoading: true,
    profileLoading: true,
    profile: null,
    userReferralStats: null,
    inventory: null,
    myStats: {
      spent: 0,
      won: 0,
      totalCaseOpenings: 0,
      totalUpgrades: 0,
      totalContracts: 0,
    },
    eventStats: null,
    prizeItemData: {
      leveledUp: false,
      items: [],
      openAgainConfig: null,
    },
    deposits: [],
    depositsPagination: null,
    depositStreak: null,
    stats: null,
    redepositNextAvailableAt: null,
  }),

  actions: {
    setProfile(profile: User | null) {
      this.profile = profile

      if (!profile) {
        this.setDeposits()
        this.setDepositsPagination(null)
      }

      if (profile?.id) {
        const featureFlagsStore = useFeatureFlagsStore()
        featureFlagsStore.updateContext({
          userId: profile.id,
          properties: {
            steamId: profile.steamId,
            username: profile.username,
            isVip: profile.isVip ? 'true' : 'false',
            countryCode: profile.countryCode,
            currentLevel: String(profile.currentLevel ?? 0),
            steamLevel: String(profile.steamLevel ?? 0),
          },
        })
      }
    },
    setInventory(inventory: any) {
      this.inventory = inventory
    },
    setMyStats(stats: ProfileState['myStats']) {
      this.myStats = stats
    },
    setEventStats(stats: UserWPStats | null) {
      this.eventStats = stats
    },
    setDeposits(deposits: DepositHistoryProfile[] = []) {
      this.deposits = deposits
    },
    setDepositsPagination(pagination: ProfileDepositsResponse['pagination'] | null) {
      this.depositsPagination = pagination
    },
    setDepositStreak(streak: DepositStreakStatusResponse | null) {
      this.depositStreak = streak
    },
    setStats(stats: UserStats | null) {
      this.stats = stats
    },
    setReDepositNextAvailableAt(value: string | number | null) {
      this.redepositNextAvailableAt = value
    },
    setProfilePageLoading(loading: boolean) {
      this.profilePageLoading = loading
    },
    setPrizeItemData(data: PrizeItemData) {
      this.prizeItemData = data
    },
    // Services
    async fetchProfile() {
      this.profilePageLoading = true
      this.profileLoading = true
      try {
        const { user, openings, myStats, depositStreak, stats, redepositNextAvailableAt } =
          await getProfile()
        this.setProfile(user)
        this.setMyStats({
          spent: openings?.spent ?? 0,
          won: openings?.won ?? 0,
          totalCaseOpenings: openings?.totalCaseOpenings ?? 0,
          totalUpgrades: openings?.totalUpgrades ?? 0,
          totalContracts: openings?.totalContracts ?? 0,
        })
        this.setEventStats(myStats || null)
        this.setDepositStreak(depositStreak ?? null)
        this.setStats(stats ?? null)
        this.setReDepositNextAvailableAt(redepositNextAvailableAt ?? null)
      } catch (error) {
        console.error('Error fetching profile:', error)
      } finally {
        this.profilePageLoading = false
        this.profileLoading = false
      }
    },
    async fetchProfileDeposits(params: ProfileDepositsParams = { page: 1, pageSize: 20 }) {
      try {
        const response = await getProfileDeposits(params)
        this.setDeposits(response.result ?? [])
        this.setDepositsPagination(response.pagination)
        return response
      } catch (error) {
        console.error('Error fetching profile deposits:', error)
        this.setDeposits()
        this.setDepositsPagination(null)
        throw error
      }
    },
    async fetchInventory() {
      try {
        const response = await InventoryService.getV1Inventory()
        this.setInventory(response)
      } catch (error) {
        console.error('Error fetching inventory:', error)
      } finally {
      }
    },
    async saveTradeUrl(tradeUrl: string) {
      try {
        const response = await ProfileService.postV1ProfileMeTradeUrl({ tradeUrl })
        if (response.success) {
          notify('Trade URL saved successfully', 'success')
        }

        this.fetchProfile()
      } catch (error) {
        console.error('Error saving trade URL:', error)
        notify('Error saving trade URL', 'error')
      }
    },
    async saveEmail(email: string) {
      try {
        await ProfileService.postV1ProfileMeEmail({ email })
        notify('Email saved successfully. Please check your inbox for verification.', 'success')
        this.fetchProfile()
      } catch (error) {
        console.error('Error saving email:', error)
        notify('Error saving email', 'error')
        throw error
      }
    },
    async sellItem(itemId: string, itemPrice: number) {
      try {
        await InventoryService.postV1InventorySell({
          inventoryItemIds: [itemId],
        })
        notify(`Item sold for $${(itemPrice / 100).toFixed(2)}`, 'success')
        // Refresh inventory
        this.fetchInventory()
        this.fetchProfile()
      } catch (error) {
        console.error('Error selling item:', error)
        notify('Error selling item', 'error')
      }
    },
    async sellItems(itemIds: string[], totalPrice: number) {
      try {
        await InventoryService.postV1InventorySell({
          inventoryItemIds: itemIds,
        })
        notify(`Items sold for $${(totalPrice / 100).toFixed(2)}`, 'success')
        // Refresh inventory
        this.fetchInventory()
        this.fetchProfile()
      } catch (error) {
        console.error('Error selling items:', error)
        notify('Error selling items', 'error')
      }
    },
    updateInventoryItem(item: any) {
      if (!this.inventory) {
        this.inventory = [item]
        return
      }
      const i = this.inventory.findIndex((it) => it.id === item.id)
      if (i > -1) this.inventory[i] = item
      else this.inventory.push(item)
    },
    async fetchUserReferralStats() {
      try {
        const response = await getReferralStats()
        this.userReferralStats = response
      } catch (error) {
        console.error('Error fetching referral stats:', error)
      }
    },
    async createReferralCode(referralCode: string) {
      try {
        await createReferralCode(referralCode)
        notify('Referral code created successfully', 'success')
      } catch (error) {
        console.error('Error creating referral code:', error)
        notify('Error creating referral code', 'error')
      }
    },
    async useReferralCode(referralCode: string) {
      try {
        await useReferralCode(referralCode)
        notify('Referral code used successfully', 'success')
        this.fetchProfile()
      } catch (error: any) {
        console.error('Error using referral code:', error)
        const errorMessage = error?.message || 'Error using referral code'
        notify(errorMessage, 'error')
        throw error
      }
    },
    async claimReferralEarnings() {
      try {
        const response = await claimReferralEarnings()
        notify('Referral earnings claimed successfully', 'success')
        await this.fetchProfile()
        await this.fetchUserReferralStats()
        return response
      } catch (error: any) {
        console.error('Error claiming referral earnings:', error)
        const errorMessage = error?.message || 'Error claiming referral earnings'
        notify(errorMessage, 'error')
        throw error
      }
    },
    async claimDepositStreak() {
      try {
        const response = await claimDepositStreak()
        notify('Reward claimed successfully!', 'success')
        await this.fetchProfile()
        return response
      } catch (error: any) {
        console.error('Error claiming deposit streak:', error)
        const errorMessage = error?.message || 'Error claiming reward'
        notify(errorMessage, 'error')
        throw error
      }
    },
    async updateWallet() {
      try {
        const { balance, caseToken } = await getBalance()
        if (this.profile) {
          this.profile.balance = balance
          this.profile.caseToken = caseToken
        }
      } catch (error) {
        console.error('Error updating wallet:', error)
      }
    },
  },
})
